import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useFleetApi, type RuntimeStatus } from "./api";

function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Runtime() {
  const { token, logout } = useAuth();
  const { fetchRuntime } = useFleetApi(token, logout);
  const [data, setData] = useState<RuntimeStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      fetchRuntime()
        .then(d => { if (alive) setData(d); })
        .catch(e => { if (alive) setErr(String(e)); });
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [fetchRuntime]);

  if (err) {
    return (
      <section>
        <p style={{ color: "var(--crit)" }}>{err}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section>
        <p className="muted">Loading runtime…</p>
      </section>
    );
  }

  const pending = data.tasks_by_status.pending ?? 0;
  const running = data.tasks_by_status.running ?? 0;
  const resolved = data.tasks_by_status.resolved ?? 0;
  const failed = data.tasks_by_status.failed ?? 0;

  return (
    <section>
      <span className="meta">live · refreshes every 15s</span>
      <p className="page-lede">
        Substrate health: task queue depth, throughput in the last hour, and the most recent
        failures from <code>fleet.task</code>.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        <Stat label="Pending" value={pending} tone={pending > 10 ? "warn" : "ok"} />
        <Stat label="Running" value={running} />
        <Stat label="Resolved (1h)" value={data.last_hour.resolved} tone="ok" />
        <Stat label="Failed (1h)" value={data.last_hour.failed}
              tone={data.last_hour.failed > 0 ? "crit" : "ok"} />
      </div>

      <div className="card" style={{ marginBottom: "12px" }}>
        <h3>Cumulative task status</h3>
        <div className="chips" style={{ marginTop: "6px" }}>
          <span className="chip">pending: <strong>{pending}</strong></span>
          <span className="chip">running: <strong>{running}</strong></span>
          <span className="chip">resolved: <strong>{resolved}</strong></span>
          <span className="chip">failed: <strong>{failed}</strong></span>
        </div>
        {data.pending.oldest_at && (
          <p className="muted" style={{ marginTop: "8px", fontSize: "12px" }}>
            Oldest pending task created {fmtAgo(data.pending.oldest_at)}.
          </p>
        )}
      </div>

      <div className="card">
        <h3>Recent failures</h3>
        {data.recent_failures.length === 0 ? (
          <p className="muted">No failures recorded.</p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto auto 1fr",
              rowGap: "8px",
              columnGap: "14px",
              marginTop: "8px",
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-3)" }}>ID</div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-3)" }}>Callee · when</div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-3)" }}>Failure</div>
            {data.recent_failures.map(f => (
              <FailureRow key={f.id} {...f} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

interface StatProps {
  label: string;
  value: number;
  tone?: "ok" | "warn" | "crit";
}

function Stat({ label, value, tone }: StatProps) {
  const toneColor = tone === "crit" ? "var(--crit)" : tone === "warn" ? "var(--warn)" : "var(--text)";
  return (
    <div className="card" style={{ padding: "14px 16px", minHeight: "78px" }}>
      <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "0.06em", color: "var(--text-3)" }}>{label}</div>
      <div style={{ fontSize: "26px", fontWeight: 600, color: toneColor,
                    fontVariantNumeric: "tabular-nums", lineHeight: 1.15, marginTop: "4px" }}>
        {value}
      </div>
    </div>
  );
}

interface FailureProps {
  id: number;
  callee: string;
  failure_reason: string | null;
  created_at: string;
}

function FailureRow({ id, callee, failure_reason, created_at }: FailureProps) {
  const [expanded, setExpanded] = useState(false);
  const reason = failure_reason ?? "";
  const truncated = reason.length > 120 && !expanded;
  return (
    <>
      <div style={{ fontVariantNumeric: "tabular-nums" }}>
        <Link to={`/fleet/tasks?status=failed#${id}`}>#{id}</Link>
      </div>
      <div style={{ fontSize: "12.5px", color: "var(--text-2)" }}>
        <code>{callee}</code>
        <span style={{ marginLeft: "8px", color: "var(--text-3)" }}>{fmtAgo(created_at)}</span>
      </div>
      <div
        style={{
          fontSize: "12px",
          color: "var(--text-2)",
          fontFamily: "var(--mono)",
          whiteSpace: "pre-wrap",
          cursor: reason.length > 120 ? "pointer" : "default",
        }}
        onClick={() => reason.length > 120 && setExpanded(x => !x)}
      >
        {truncated ? reason.slice(0, 120) + "…" : reason}
      </div>
    </>
  );
}
